import { CloseButton, SettingsButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { IComparisonPopulationSetting } from '../../../../data/providers/settings/comparisonPopulationSettingsProvider';
import {
  DateRangeTypeOption,
  IDateRange,
  IExclusiveDateOption,
  IInclusiveDateOption,
} from '../../../../data/providers/settings/dates/dateRangeProvider';
import {
  IQuadrantReportSettings,
  IQuadrantReportSettingsState,
  quadrantReportSettingsProvider,
} from '../../../../data/providers/settings/reports/quadrantReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { isValidComparisonPopulation } from '../../../../data/providers/utils/isValidComparisonPopulation';
import { isValidDateRange } from '../../../../data/providers/utils/isValidDateRange';
import { IRange, ISelectOption } from '../../../../data/types';
import {
  deleteSelectedTemplate,
  saveQuadrantSettings,
} from '../../../../data/writers/settings/reports/quadrantReportSettings';
import { Row } from '../../../layout/Row';
import { ComparisonSettings } from '../../../settings/comparison/ComparisonSettings';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { DateSelect } from '../../../shared/form/selects/DateRangeSelect';
import { Step } from '../../../shared/form/Step';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { FormSection } from '../../utils/settings/FormSection';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateSelect } from '../../utils/settings/TemplateSelect';
import { TemplateForm } from './TemplateForm';

interface IPopupProps {
  onClose: () => void;
  onSave: () => void;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ISettingsTemplate<IQuadrantReportSettings>>();
  const [templates, setTemplates] = React.useState<
    Array<ISettingsTemplate<IQuadrantReportSettings>>
  >([]);
  const [initialTemplate, setInitialTemplate] =
    React.useState<ISettingsTemplate<IQuadrantReportSettings>>();
  const [timeframe, setTimeframe] = React.useState<IDateRange>();
  const [comparisonPopulation, setComparisonPopulation] =
    React.useState<IComparisonPopulationSetting>();

  const [saving, setSaving] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);

  /**
   * @description subscribe to quadrant report settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = quadrantReportSettingsProvider().subscribe(
      (mSettings: IQuadrantReportSettingsState): void => {
        setTemplates(mSettings?.templates);
        setSelectedTemplate(mSettings?.selected);
        setInitialTemplate(mSettings?.selected);

        /// do not override local state if no values are defined
        if (!isEmpty(mSettings?.comparisonPopulation)) {
          setComparisonPopulation(mSettings.comparisonPopulation);
        }
        if (!isEmpty(mSettings?.timeframe)) {
          setTimeframe(mSettings.timeframe);
        }

        setLoading(false);
      }
    );

    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    setSaving(true);
    if (!isEqual(initialTemplate?.id, selectedTemplate?.id)) {
      props.onSave();
    }
    await saveQuadrantSettings(
      selectedTemplate,
      timeframe,
      comparisonPopulation
    );
    setSaving(false);
    props.onClose();
  };

  /**
   * @description handle changes to the date range
   * @param dateType
   * @param dateRange
   */
  const dateRangeChangeHandler = (
    dateType: DateRangeTypeOption,
    dateRange: IRange
  ): void => {
    setTimeframe({
      dateType: dateType as IInclusiveDateOption,
      dateRange,
    });
  };

  /**
   * @description handles change to the comparsion population entity
   * @param entity
   */
  const handleComparisonEntityChange = (entity: ISelectOption): void => {
    setComparisonPopulation({
      ...comparisonPopulation,
      entity,
    });
  };

  /**
   * @description handles date change
   */

  const handleComparisonDateChange = (
    dateRangeType: IExclusiveDateOption,
    dateRangeValues: IRange
  ): void => {
    setComparisonPopulation({
      ...comparisonPopulation,
      dateRangeType,
      dateRangeValues,
    });
  };

  /**
   * @description handles change to the selected template
   * @param mSelectedTemplate
   */
  const templateChangeHandler = (
    mSelectedTemplate: ISettingsTemplate<IQuadrantReportSettings>
  ): void => {
    setSelectedTemplate(mSelectedTemplate);
  };

  /**
   * @description deletes the supplied template and finds a suitable replacement for the selected template
   * @param template
   */
  const templateDeleteHandler = async (
    template: ISettingsTemplate<IQuadrantReportSettings>
  ): Promise<void> => {
    const index = templates.findIndex(
      (tmp: ISettingsTemplate<IQuadrantReportSettings>): boolean =>
        tmp.id === template.id
    );
    /// get either the template before or after the template being deleted
    const replacement: ISettingsTemplate<IQuadrantReportSettings> =
      index === 0 ? templates?.[1] : templates?.[index - 1];

    await deleteSelectedTemplate(template, replacement);
  };

  const isValid = React.useMemo((): boolean => {
    /// make sure user has selected a valid timeframe
    if (!isValidDateRange(timeframe)) return false;
    /// make sure user has selected a template
    if (isEmpty(selectedTemplate)) return false;
    /// make sure user selected a valid comparison population
    if (!isValidComparisonPopulation(comparisonPopulation, true)) return false;
    /// form is valid and can be submitted
    return true;
  }, [
    JSON.stringify(timeframe),
    JSON.stringify(selectedTemplate),
    JSON.stringify(comparisonPopulation),
  ]);

  return (
    <PopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Quadrant Report Settings</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading || saving ? (
            <LinearLoading />
          ) : (
            <React.Fragment>
              <Row shouldCollapse style={{ height: '100%' }}>
                <LeftColumn>
                  <TemplateSelect
                    step={1}
                    templates={templates}
                    onSelect={templateChangeHandler}
                    onDelete={templateDeleteHandler}
                    selectedTemplateId={selectedTemplate?.id}
                    formComponent={TemplateForm}
                    reportType={'Quadrant Report'}
                    reportTypeId='quadrantReport'
                  />
                </LeftColumn>
                <RightColumn>
                  <FormSection>
                    <CardHeader>
                      <CardHeaderLeft>
                        <Step>2</Step>
                        <h6>Select Timeframe</h6>
                      </CardHeaderLeft>
                    </CardHeader>
                    <div style={{ padding: '10px' }}>
                      <ControlSpacer>
                        <DateSelect
                          type='inclusive'
                          dateRangeType={timeframe?.dateType}
                          dateRange={timeframe?.dateRange}
                          onChange={dateRangeChangeHandler}
                        />
                      </ControlSpacer>
                    </div>
                  </FormSection>
                  <FormSection>
                    <CardHeader>
                      <CardHeaderLeft>
                        <Step>3</Step>
                        <h6>Select Comparison Population</h6>
                      </CardHeaderLeft>
                    </CardHeader>
                    <ComparisonSettings
                      zscoreDisabled
                      handleEntityChange={handleComparisonEntityChange}
                      handleDateChange={handleComparisonDateChange}
                      settings={comparisonPopulation}
                    />
                  </FormSection>
                </RightColumn>
              </Row>
            </React.Fragment>
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton onClick={handleSave} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  onSave: () => void;
}
export const QuadrantSettings = withRouter(
  (props: IProps): React.ReactElement => {
    const open = (): void =>
      openPopup(
        <Popup onClose={closePopup(props.history)} onSave={props.onSave} />,
        props.history,
        'quadrant-report-settings'
      );

    return <SettingsButton onClick={open} />;
  }
);
